const NAVIGATION_KEYS = ['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab', 'Home', 'End'];

/**
 * One-time-password entry with auto-submit and a resend countdown. Expects the text input as
 * `x-ref="otpInput"` and the Livewire component to expose `verify(code)` and `resend()`.
 *
 * @param {{ length: number, timer: number }} config `timer` is the resend cooldown in seconds.
 */
export default function otpInput({ length, timer }) {
    let timerInterval = null;

    return {
        otp: '',
        length,
        timerSeconds: timer,
        isSubmitting: false,

        init() {
            this.startTimer();
            this.$nextTick(() => this.focusInput());
        },

        destroy() {
            clearInterval(timerInterval);
        },

        get canResend() {
            return this.timerSeconds <= 0;
        },

        get timerLabel() {
            const minutes = String(Math.floor(this.timerSeconds / 60)).padStart(2, '0');
            const seconds = String(this.timerSeconds % 60).padStart(2, '0');

            return `${minutes}:${seconds}`;
        },

        isDotFilled(index) {
            return this.otp.length >= index;
        },

        allowOnlyDigits(event) {
            if (NAVIGATION_KEYS.includes(event.key) || event.ctrlKey || event.metaKey) {
                return;
            }

            if (!/^[0-9]$/.test(event.key)) {
                event.preventDefault();
            }
        },

        focusInput() {
            this.$refs.otpInput?.focus();
        },

        handleInput(value) {
            this.otp = value.replace(/\D/g, '').slice(0, this.length);
            this.autoVerifyIfComplete();
        },

        handlePaste(event) {
            const pasted = (event.clipboardData?.getData('text') ?? '').trim();

            event.preventDefault();

            if (!/^\d+$/.test(pasted)) {
                return;
            }

            this.otp = pasted.slice(0, this.length);
            this.focusInput();
            this.autoVerifyIfComplete();
        },

        autoVerifyIfComplete() {
            if (this.otp.length === this.length) {
                this.verify();
            }
        },

        async verify() {
            if (this.otp.length !== this.length || this.isSubmitting) {
                return;
            }

            this.isSubmitting = true;

            try {
                const isVerified = await this.$wire.verify(this.otp);

                if (!isVerified) {
                    this.isSubmitting = false;
                }
            } catch {
                this.isSubmitting = false;
            }
        },

        async resendCode() {
            if (!this.canResend || this.isSubmitting) {
                return;
            }

            this.isSubmitting = true;

            try {
                await this.$wire.resend();
                this.restartTimer();
            } catch {
                // The Livewire component reports resend failures to the user.
            } finally {
                this.isSubmitting = false;
            }
        },

        resetOtpUi() {
            this.otp = '';
            this.restartTimer();
        },

        restartTimer() {
            this.timerSeconds = timer;
            this.startTimer();
        },

        startTimer() {
            clearInterval(timerInterval);

            if (this.timerSeconds <= 0) {
                return;
            }

            timerInterval = setInterval(() => {
                this.timerSeconds = Math.max(0, this.timerSeconds - 1);

                if (this.timerSeconds === 0) {
                    clearInterval(timerInterval);
                }
            }, 1000);
        },
    };
}
